import { Breadcrumbs, CircularProgress, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { matchPath } from 'react-router'
import { useLocation, useParams } from 'react-router-dom'

import { BreadcrumbLink } from 'components/common/BreadcrumbLink'
import { useGetConfigurationsUrls } from 'components/configurations/hooks/use-get-configurations-urls'
import Icon, { IconName } from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { useGetSharedAssets } from 'queries/sharedAssets/use-get-shared-assets'

const ROOT_URLS = ['agents', 'clusters', 'private-network-integrations']

export const ConfigurationsBreadcrumbs: React.FC = () => {
  const location = useLocation()
  const { assetId } = useParams()
  const configurationsUrls = useGetConfigurationsUrls()

  const { pathname } = location

  const match = matchPath(`${configurationsUrls.base()}/*`, pathname)
  const matchParams = match?.params['*']

  const isRootUrl = ROOT_URLS.includes(matchParams)

  const { data: sharedAssetById, isLoading: isLoadingSharedAsset } =
    useGetSharedAssets({
      enabled: Boolean(assetId)
    })

  const rootBreadcrumb: { name: string; icon: IconName; to: string } =
    useMemo(() => {
      const matchParamsRoot = matchParams?.split('/')[0] ?? 'agents'

      switch (matchParamsRoot) {
        case 'clusters':
          return {
            name: 'Clusters',
            icon: 'dharmachakra',
            to: configurationsUrls.clusters()
          }
        case 'private-network-integrations':
          return {
            name: 'Private Network Integrations',
            icon: 'lock',
            to: configurationsUrls.privateNetworkIntegrations()
          }
        default:
          return {
            name: 'Agents',
            icon: 'clients',
            to: configurationsUrls.agents()
          }
      }
    }, [configurationsUrls, matchParams])

  const assetName = useMemo(() => {
    if (!assetId) {
      return null
    }

    const sharedAsset = sharedAssetById?.myClusters.find(
      (asset) => asset.assetId === assetId
    )

    if (!sharedAsset) {
      return assetId // Fallback to displaying the assetId param if sharedAsset is not found
    }

    return sharedAsset.clusterName
  }, [assetId, sharedAssetById?.myClusters])

  return (
    <Breadcrumbs aria-label="breadcrumb">
      {[
        isRootUrl ? (
          <Typography key="root" color="text.primary">
            <Icon
              css={{ marginRight: muiTheme.spacing(1) }}
              icon={rootBreadcrumb.icon}
            />
            {rootBreadcrumb.name}
          </Typography>
        ) : (
          <BreadcrumbLink
            key="root"
            icon={rootBreadcrumb.icon}
            name={rootBreadcrumb.name}
            to={rootBreadcrumb.to}
          />
        ),
        assetId && [
          <Typography key="manage-access">Manage Access</Typography>,
          <Typography key="asset-name">
            {isLoadingSharedAsset ? (
              <CircularProgress size={muiTheme.spacing(2)} />
            ) : (
              assetName
            )}
          </Typography>
        ]
      ]}
    </Breadcrumbs>
  )
}
